import { z } from "zod/v4";

/**
 * `WasteSensePayload` — the structured `payload.json` the frontend renders into
 * the customer's weekly WasteSense report. This is the engine's step-7
 * validation gate: the assembled payload MUST decode against this schema or the
 * message hard-fails to DLQ.
 *
 * ## The redesign (2026-07)
 *
 * The report was re-centred on **operator-confirmed dumps** and physical media
 * volume, replacing the old AI-funnel framing:
 *   - `kpis` now report media/evidence volume — clips recorded, snapshots
 *     captured, and confirmed dumps — instead of the VCS event-funnel numbers.
 *   - A new `dump_detections[]` block carries each operator-confirmed dump with
 *     the identifiers the UI needs to play the evidence clip (via the existing
 *     `useClips` path — see the field docs below).
 *   - `map_sites[]` keeps the per-site markers but swaps its metrics to the same
 *     three counts, scoped to that site.
 *   - The old `priority_incidents[]` and `site_logs[]` blocks (AI incident hero
 *     + per-site event log) are removed — neither is in the new design.
 *
 * There is no back-compat and no schema-version bump: the old shape is replaced
 * outright and all stored reports are regenerated.
 */

/** Bumped on breaking payload-schema changes. Left at 1: the redesign replaces
 * the shape wholesale and all reports are regenerated, so no consumer straddles
 * both versions (design decision, 2026-07). */
export const WASTESENSE_PAYLOAD_SCHEMA_VERSION = 1;

export const WasteSenseReportMeta = z
  .object({
    customer_name: z.string(),
    customer_logo_url: z.string().optional(),
    report_month_year: z.string(),
    report_title: z.string(),
    report_subtitle: z.string(),
    window_label: z.string(),
    window_days: z.number().int().min(1),
  })
  .strict();
export type WasteSenseReportMeta = z.infer<typeof WasteSenseReportMeta>;

/**
 * The three headline numbers at the top of the report (the "Weekly summary").
 * All are tenant-wide totals over the reporting window:
 *   - `clips_recorded`     — video clips recorded across all subscribed assets
 *   - `snapshots_captured` — still images captured across all subscribed assets
 *   - `confirmed_dumps`    — operator-confirmed dumps (the `dump_detections[]`
 *                            count); the customer-facing section is titled
 *                            "Dump Detections" but the count is confirmed dumps
 *                            (see `features/a3s-dumping/CONTEXT.md` on why we
 *                            avoid "detection" for operator-asserted evidence).
 */
export const WasteSenseKpis = z
  .object({
    clips_recorded: z.number().int().min(0),
    snapshots_captured: z.number().int().min(0),
    confirmed_dumps: z.number().int().min(0),
  })
  .strict();
export type WasteSenseKpis = z.infer<typeof WasteSenseKpis>;

/**
 * One operator-confirmed dump, carrying everything the UI needs to (a) display
 * the dump detail and (b) resolve the evidence clip for playback.
 *
 * Clip playback reuses the existing media path (`useClips` → `ClipVideoPlayer`,
 * as in `DumpingSearch.web.tsx`) rather than a bespoke presign endpoint: the UI
 * queries `useClips({ siteId: site_id, assetCameraKey: asset_camera_key,
 * startTime: clip_timestamp, endTime: <clip_timestamp + small window> })` and
 * plays the clip whose `startTime` matches `clip_timestamp`. So the payload
 * carries clip *identifiers*, not a resolved URL or S3 key — the presigned URL
 * is resolved at view time (URLs are short-lived; baking one into a stored
 * report would expire).
 *
 * A `ConfirmedDump` identifies its clip by camera + ISO `timestamp` (clips have
 * no separate id); `asset_camera_key` is `{asset}@{camera}` (the `AssetCameraKey`
 * the media route expects) and `clip_timestamp` is that ISO start time.
 */
export const WasteSenseDumpDetection = z
  .object({
    /** Stable identity for the card — `{asset_camera_key}#{clip_timestamp}`. */
    dump_id: z.string(),
    site_id: z.string(),
    site_name: z.string(),
    /** `{asset}@{camera}` — the `AssetCameraKey` the media `useClips` route keys on. */
    asset_camera_key: z.string(),
    /** ISO-8601 UTC clip start time — the clip identity for `useClips`. */
    clip_timestamp: z.string(),
    /** Human-facing "detected at" label in the site's local zone. */
    detected_at_label: z.string(),
    /** Human-facing object label (resolved from the dumping object taxonomy). */
    object_label: z.string(),
    /** Free-text operator description; empty string when none was provided. */
    description: z.string(),
  })
  .strict();
export type WasteSenseDumpDetection = z.infer<typeof WasteSenseDumpDetection>;

/**
 * One map marker per site with that site's three headline counts. The old
 * `camera_count` / `total_observations` / `priority_incidents` fields are gone;
 * the metrics now mirror the `kpis` block, scoped to the site.
 *
 * Note this is now per-**site** (one marker per site), not per-asset as in the
 * old schema — the redesign dropped the per-asset "camera unit" markers.
 */
export const WasteSenseMapSite = z
  .object({
    site_id: z.string(),
    site_title: z.string(),
    site_address: z.string(),
    clips_recorded: z.number().int().min(0),
    snapshots_captured: z.number().int().min(0),
    confirmed_dumps: z.number().int().min(0),
    map_lat: z.number(),
    map_lon: z.number(),
  })
  .strict();
export type WasteSenseMapSite = z.infer<typeof WasteSenseMapSite>;

export const WasteSensePayload = z
  .object({
    report_meta: WasteSenseReportMeta,
    kpis: WasteSenseKpis,
    dump_detections: z.array(WasteSenseDumpDetection),
    map_sites: z.array(WasteSenseMapSite),
  })
  .strict();

export type WasteSensePayload = z.infer<typeof WasteSensePayload>;
